import type { ReactNode } from "react";
import {
  type IconNameEnum,
  useDisruptionStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import { CoreDisruptionComponent } from "@b2bportal/core-types";
import { Dialog, DialogContent, Icon } from "@b2bportal/core-ui";
import { useDeviceType } from "@b2bportal/core-utilities";
import DisruptionExerciseFooter from "../DisruptionExerciseFooter/DisruptionExerciseFooter";
import defaultStyles from "./DisruptionExercisePopup.module.scss";

export type DisruptionExercisePopupHeaderIconVariant =
  | "baggage"
  | "return"
  | "moreHelp";

export interface DisruptionExercisePopupProps {
  open: boolean;
  onClose?: () => void;
  headerImage: IconNameEnum;
  headerIconVariant?: DisruptionExercisePopupHeaderIconVariant;
  title: string;
  subtitle: string;
  content: ReactNode;
  onSupportClick?: () => void;
  termsLink: string;
  onRefundClick?: () => void;
  width: string;
  hideFooter: boolean;
}

export const DisruptionExercisePopup = ({
  open,
  onClose,
  headerImage,
  title,
  subtitle,
  content,
  onSupportClick,
  termsLink,
  onRefundClick,
  width = "634px",
  hideFooter = false,
  headerIconVariant,
}: DisruptionExercisePopupProps) => {
  const image = <Icon iconName={headerImage} />;
  const COMPONENT_KEY = CoreDisruptionComponent.DisruptionExercisePopup;
  const styles = useDisruptionStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const { isDesktopAndUp } = useDeviceType();

  const headerIconModifiers =
    headerIconVariant != null ? { [headerIconVariant]: true } : {};

  const popupContent = !isDesktopAndUp ? (
    <div className={cn("half-sheet-content-mobile", headerIconModifiers)}>
      <div className={cn("header-image-container-mobile")}>{image}</div>
      <div className={cn("half-sheet-title-mobile")}>{title}</div>
      <div className={cn("half-sheet-subtitle-mobile")}>{subtitle}</div>
      <div className={cn("content-mobile")}>{content}</div>
      {!hideFooter && (
        <DisruptionExerciseFooter
          onSupportClick={onSupportClick}
          onRefundClick={onRefundClick}
          termsLink={termsLink}
        ></DisruptionExerciseFooter>
      )}
    </div>
  ) : (
    <div
      className={cn("desktop-popup-container", headerIconModifiers)}
      style={{ width: width }}
    >
      <div className={cn("header-image-container")}>{image}</div>
      <div className={cn("half-sheet-content")}>
        <div className={cn("popup-title")}>{title}</div>
        <div className={cn("popup-subtitle")}>{subtitle}</div>
        <div className={cn("content")}>{content}</div>
      </div>

      {onSupportClick && (
        <div className={cn("popup-terms-container")}>
          <DisruptionExerciseFooter
            onSupportClick={onSupportClick}
            onRefundClick={onRefundClick}
            termsLink={termsLink}
          ></DisruptionExerciseFooter>
        </div>
      )}
    </div>
  );

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogContent>{popupContent}</DialogContent>
    </Dialog>
  );
};

export default DisruptionExercisePopup;
